import {
  MentionEntityKind,
  MentionPartyRole,
  MentionRole,
  MentionStatus,
} from '@common/interfaces/mention'
import { ILoggedInteractionPayload } from '@common/interfaces/overview'
import type { RawDraftContentState } from 'draft-js'
import { IApi } from '../../api'
import { createRequestTypes } from '../../helpers/helpers'
import { createAction } from '../types'

export const prefix = 'crm'

export const [LIST_ORGANIZATIONS_REQUEST, LIST_ORGANIZATIONS_SUCCESS, LIST_ORGANIZATIONS_FAILURE] =
  createRequestTypes(prefix, 'LIST_ORGANIZATIONS')

export const listOrganizations = createAction((data: object = {}) => ({
  type: [LIST_ORGANIZATIONS_REQUEST, LIST_ORGANIZATIONS_SUCCESS, LIST_ORGANIZATIONS_FAILURE],
  promise: (api: IApi) => api.crm.listOrganizations(data),
  params: data,
}))

export const [LIST_COMPANIES_REQUEST, LIST_COMPANIES_SUCCESS, LIST_COMPANIES_FAILURE] =
  createRequestTypes(prefix, 'LIST_COMPANIES')

export const listCompanies = createAction((data: object = {}) => ({
  type: [LIST_COMPANIES_REQUEST, LIST_COMPANIES_SUCCESS, LIST_COMPANIES_FAILURE],
  promise: (api: IApi) => api.crm.listCompanies(data),
  params: data,
}))

// The company picker's option list — its own slice so loading it (from My Tasks,
// a task project, the recurrence modal) never overwrites the CRM Companies tab's
// filtered, paginated list, which shares this domain.
export const [
  LIST_COMPANY_OPTIONS_REQUEST,
  LIST_COMPANY_OPTIONS_SUCCESS,
  LIST_COMPANY_OPTIONS_FAILURE,
] = createRequestTypes(prefix, 'LIST_COMPANY_OPTIONS')

export const listCompanyOptions = createAction(() => ({
  type: [LIST_COMPANY_OPTIONS_REQUEST, LIST_COMPANY_OPTIONS_SUCCESS, LIST_COMPANY_OPTIONS_FAILURE],
  promise: (api: IApi) => api.crm.listCompanyOptions(),
}))

// The organization half of the task Company / Org filter's options; own slice for
// the same reason as companyOptions.
export const [
  LIST_ORGANIZATION_OPTIONS_REQUEST,
  LIST_ORGANIZATION_OPTIONS_SUCCESS,
  LIST_ORGANIZATION_OPTIONS_FAILURE,
] = createRequestTypes(prefix, 'LIST_ORGANIZATION_OPTIONS')

export const listOrganizationOptions = createAction(() => ({
  type: [
    LIST_ORGANIZATION_OPTIONS_REQUEST,
    LIST_ORGANIZATION_OPTIONS_SUCCESS,
    LIST_ORGANIZATION_OPTIONS_FAILURE,
  ],
  promise: (api: IApi) => api.crm.listOrganizationOptions(),
}))

export const [LIST_PEOPLE_REQUEST, LIST_PEOPLE_SUCCESS, LIST_PEOPLE_FAILURE] = createRequestTypes(
  prefix,
  'LIST_PEOPLE',
)

export const listPeople = createAction((data: object = {}) => ({
  type: [LIST_PEOPLE_REQUEST, LIST_PEOPLE_SUCCESS, LIST_PEOPLE_FAILURE],
  promise: (api: IApi) => api.crm.listPeople(data),
  params: data,
}))

export const [LIST_NETWORK_REQUEST, LIST_NETWORK_SUCCESS, LIST_NETWORK_FAILURE] =
  createRequestTypes(prefix, 'LIST_NETWORK')

// Generic loader for every relationship rail. `railKey` (kind:ownerId) namespaces
// the reducer bucket so multiple rails on one page don't clobber each other;
// `loadMore` flows through for the infinite-scroll append.
export const listNetwork = createAction((kind: string, ownerId: string, params: object = {}) => ({
  type: [LIST_NETWORK_REQUEST, LIST_NETWORK_SUCCESS, LIST_NETWORK_FAILURE],
  promise: (api: IApi) => api.crm.listNetwork(kind, ownerId, params),
  params: { ...params, railKey: `${kind}:${ownerId}` },
}))

export const [SHOW_ORGANIZATION_REQUEST, SHOW_ORGANIZATION_SUCCESS, SHOW_ORGANIZATION_FAILURE] =
  createRequestTypes(prefix, 'SHOW_ORGANIZATION')

export const showOrganization = createAction(
  (id: string, source: 'organization' | 'entity' = 'organization') => ({
    type: [SHOW_ORGANIZATION_REQUEST, SHOW_ORGANIZATION_SUCCESS, SHOW_ORGANIZATION_FAILURE],
    promise: (api: IApi) => api.crm.showOrganization(id, source),
  }),
)

export const [
  UPDATE_ORGANIZATION_REQUEST,
  UPDATE_ORGANIZATION_SUCCESS,
  UPDATE_ORGANIZATION_FAILURE,
] = createRequestTypes(prefix, 'UPDATE_ORGANIZATION')

export const updateOrganization = createAction((id: string, data: object) => ({
  type: [UPDATE_ORGANIZATION_REQUEST, UPDATE_ORGANIZATION_SUCCESS, UPDATE_ORGANIZATION_FAILURE],
  promise: (api: IApi) => api.crm.updateOrganization(id, data),
}))

export const [
  LOAD_ORGANIZATION_FIELD_OPTIONS_REQUEST,
  LOAD_ORGANIZATION_FIELD_OPTIONS_SUCCESS,
  LOAD_ORGANIZATION_FIELD_OPTIONS_FAILURE,
] = createRequestTypes(prefix, 'LOAD_ORGANIZATION_FIELD_OPTIONS')

export const loadOrganizationFieldOptions = createAction((id: string) => ({
  type: [
    LOAD_ORGANIZATION_FIELD_OPTIONS_REQUEST,
    LOAD_ORGANIZATION_FIELD_OPTIONS_SUCCESS,
    LOAD_ORGANIZATION_FIELD_OPTIONS_FAILURE,
  ],
  promise: (api: IApi) => api.crm.organizationFieldOptions(id),
}))

export const [
  LOAD_ORGANIZATION_OVERVIEW_FEED_REQUEST,
  LOAD_ORGANIZATION_OVERVIEW_FEED_SUCCESS,
  LOAD_ORGANIZATION_OVERVIEW_FEED_FAILURE,
] = createRequestTypes(prefix, 'LOAD_ORGANIZATION_OVERVIEW_FEED')

export const loadOrganizationOverviewFeed = createAction(
  (id: string, options: { skipLoader?: boolean } = {}) => ({
    type: [
      LOAD_ORGANIZATION_OVERVIEW_FEED_REQUEST,
      LOAD_ORGANIZATION_OVERVIEW_FEED_SUCCESS,
      LOAD_ORGANIZATION_OVERVIEW_FEED_FAILURE,
    ],
    promise: (api: IApi) => api.crm.organizationOverviewFeed(id),
    // Background refreshes (e.g. after a note is added) pass skipLoader so the
    // timeline doesn't flash its skeleton — it updates in place instead.
    params: { skipLoader: options.skipLoader },
  }),
)

export const [
  REGENERATE_ORGANIZATION_DOSSIER_REQUEST,
  REGENERATE_ORGANIZATION_DOSSIER_SUCCESS,
  REGENERATE_ORGANIZATION_DOSSIER_FAILURE,
] = createRequestTypes(prefix, 'REGENERATE_ORGANIZATION_DOSSIER')

export const regenerateOrganizationDossier = createAction((id: string) => ({
  type: [
    REGENERATE_ORGANIZATION_DOSSIER_REQUEST,
    REGENERATE_ORGANIZATION_DOSSIER_SUCCESS,
    REGENERATE_ORGANIZATION_DOSSIER_FAILURE,
  ],
  promise: (api: IApi) => api.crm.regenerateOrganizationDossier(id),
}))

export const [
  REGENERATE_ORGANIZATION_CATCH_UP_REQUEST,
  REGENERATE_ORGANIZATION_CATCH_UP_SUCCESS,
  REGENERATE_ORGANIZATION_CATCH_UP_FAILURE,
] = createRequestTypes(prefix, 'REGENERATE_ORGANIZATION_CATCH_UP')

export const regenerateOrganizationCatchUp = createAction((id: string) => ({
  type: [
    REGENERATE_ORGANIZATION_CATCH_UP_REQUEST,
    REGENERATE_ORGANIZATION_CATCH_UP_SUCCESS,
    REGENERATE_ORGANIZATION_CATCH_UP_FAILURE,
  ],
  promise: (api: IApi) => api.crm.regenerateOrganizationCatchUp(id),
}))

export const [
  UPDATE_ORGANIZATION_DOSSIER_REQUEST,
  UPDATE_ORGANIZATION_DOSSIER_SUCCESS,
  UPDATE_ORGANIZATION_DOSSIER_FAILURE,
] = createRequestTypes(prefix, 'UPDATE_ORGANIZATION_DOSSIER')

export const updateOrganizationDossier = createAction((id: string, dossier: string) => ({
  type: [
    UPDATE_ORGANIZATION_DOSSIER_REQUEST,
    UPDATE_ORGANIZATION_DOSSIER_SUCCESS,
    UPDATE_ORGANIZATION_DOSSIER_FAILURE,
  ],
  promise: (api: IApi) => api.crm.updateOrganizationDossier(id, dossier),
}))

export const [SHOW_COMPANY_REQUEST, SHOW_COMPANY_SUCCESS, SHOW_COMPANY_FAILURE] =
  createRequestTypes(prefix, 'SHOW_COMPANY')

export const showCompany = createAction((id: string, options: { skipLoader?: boolean } = {}) => ({
  type: [SHOW_COMPANY_REQUEST, SHOW_COMPANY_SUCCESS, SHOW_COMPANY_FAILURE],
  promise: (api: IApi) => api.crm.showCompany(id),
  // Background refreshes (e.g. after an inline field save) pass skipLoader so the
  // page doesn't flash its full-page spinner.
  params: { skipLoader: options.skipLoader },
}))

export const [CREATE_COMPANY_REQUEST, CREATE_COMPANY_SUCCESS, CREATE_COMPANY_FAILURE] =
  createRequestTypes(prefix, 'CREATE_COMPANY')

export const createCompany = createAction((data: object) => ({
  type: [CREATE_COMPANY_REQUEST, CREATE_COMPANY_SUCCESS, CREATE_COMPANY_FAILURE],
  promise: (api: IApi) => api.crm.createCompany(data),
}))

export const [CREATE_PERSON_REQUEST, CREATE_PERSON_SUCCESS, CREATE_PERSON_FAILURE] =
  createRequestTypes(prefix, 'CREATE_PERSON')

export const createPerson = createAction((data: object) => ({
  type: [CREATE_PERSON_REQUEST, CREATE_PERSON_SUCCESS, CREATE_PERSON_FAILURE],
  promise: (api: IApi) => api.crm.createPerson(data),
}))

export const [
  CREATE_ORGANIZATION_REQUEST,
  CREATE_ORGANIZATION_SUCCESS,
  CREATE_ORGANIZATION_FAILURE,
] = createRequestTypes(prefix, 'CREATE_ORGANIZATION')

export const createOrganization = createAction((data: object) => ({
  type: [CREATE_ORGANIZATION_REQUEST, CREATE_ORGANIZATION_SUCCESS, CREATE_ORGANIZATION_FAILURE],
  promise: (api: IApi) => api.crm.createOrganization(data),
}))

export const [UPDATE_COMPANY_REQUEST, UPDATE_COMPANY_SUCCESS, UPDATE_COMPANY_FAILURE] =
  createRequestTypes(prefix, 'UPDATE_COMPANY')

export const updateCompany = createAction((id: string, data: object) => ({
  type: [UPDATE_COMPANY_REQUEST, UPDATE_COMPANY_SUCCESS, UPDATE_COMPANY_FAILURE],
  promise: (api: IApi) => api.crm.updateCompany(id, data),
}))

export const [
  UPDATE_COMPANY_OWNER_REQUEST,
  UPDATE_COMPANY_OWNER_SUCCESS,
  UPDATE_COMPANY_OWNER_FAILURE,
] = createRequestTypes(prefix, 'UPDATE_COMPANY_OWNER')

export const updateCompanyOwner = createAction((data: { ownerId: string; ids: string[] }) => ({
  type: [UPDATE_COMPANY_OWNER_REQUEST, UPDATE_COMPANY_OWNER_SUCCESS, UPDATE_COMPANY_OWNER_FAILURE],
  promise: (api: IApi) => api.crm.updateCompanyOwner(data),
}))

export const [
  UPDATE_COMPANY_REVISIT_REQUEST,
  UPDATE_COMPANY_REVISIT_SUCCESS,
  UPDATE_COMPANY_REVISIT_FAILURE,
] = createRequestTypes(prefix, 'UPDATE_COMPANY_REVISIT')

export const updateCompanyRevisit = createAction(
  (data: { ids: string[]; revisitOn: string | null }) => ({
    type: [
      UPDATE_COMPANY_REVISIT_REQUEST,
      UPDATE_COMPANY_REVISIT_SUCCESS,
      UPDATE_COMPANY_REVISIT_FAILURE,
    ],
    promise: (api: IApi) => api.crm.updateCompanyRevisit(data),
  }),
)

export const [LIST_DEALS_REQUEST, LIST_DEALS_SUCCESS, LIST_DEALS_FAILURE] = createRequestTypes(
  prefix,
  'LIST_DEALS',
)

export const listDeals = createAction((companyId: string) => ({
  type: [LIST_DEALS_REQUEST, LIST_DEALS_SUCCESS, LIST_DEALS_FAILURE],
  promise: (api: IApi) => api.crm.listDeals(companyId),
}))

export const [ADVANCE_DEAL_REQUEST, ADVANCE_DEAL_SUCCESS, ADVANCE_DEAL_FAILURE] =
  createRequestTypes(prefix, 'ADVANCE_DEAL')

export const advanceDeal = createAction((id: string, data: object) => ({
  type: [ADVANCE_DEAL_REQUEST, ADVANCE_DEAL_SUCCESS, ADVANCE_DEAL_FAILURE],
  promise: (api: IApi) => api.crm.advanceDeal(id, data),
}))

export const [CLOSE_DEAL_REQUEST, CLOSE_DEAL_SUCCESS, CLOSE_DEAL_FAILURE] = createRequestTypes(
  prefix,
  'CLOSE_DEAL',
)

export const closeDeal = createAction((id: string, data: object) => ({
  type: [CLOSE_DEAL_REQUEST, CLOSE_DEAL_SUCCESS, CLOSE_DEAL_FAILURE],
  promise: (api: IApi) => api.crm.closeDeal(id, data),
}))

export const [DELETE_DEAL_REQUEST, DELETE_DEAL_SUCCESS, DELETE_DEAL_FAILURE] = createRequestTypes(
  prefix,
  'DELETE_DEAL',
)

export const deleteDeal = createAction((id: string) => ({
  type: [DELETE_DEAL_REQUEST, DELETE_DEAL_SUCCESS, DELETE_DEAL_FAILURE],
  promise: (api: IApi) => api.crm.deleteDeal(id),
}))

export const [DELETE_COMPANY_REQUEST, DELETE_COMPANY_SUCCESS, DELETE_COMPANY_FAILURE] =
  createRequestTypes(prefix, 'DELETE_COMPANY')

export const deleteCompany = createAction((id: string) => ({
  type: [DELETE_COMPANY_REQUEST, DELETE_COMPANY_SUCCESS, DELETE_COMPANY_FAILURE],
  promise: (api: IApi) => api.crm.deleteCompany(id),
}))

export const [DELETE_PERSON_REQUEST, DELETE_PERSON_SUCCESS, DELETE_PERSON_FAILURE] =
  createRequestTypes(prefix, 'DELETE_PERSON')

export const deletePerson = createAction((id: string) => ({
  type: [DELETE_PERSON_REQUEST, DELETE_PERSON_SUCCESS, DELETE_PERSON_FAILURE],
  promise: (api: IApi) => api.crm.deletePerson(id),
}))

export const [
  DELETE_ORGANIZATION_REQUEST,
  DELETE_ORGANIZATION_SUCCESS,
  DELETE_ORGANIZATION_FAILURE,
] = createRequestTypes(prefix, 'DELETE_ORGANIZATION')

export const deleteOrganization = createAction((id: string) => ({
  type: [DELETE_ORGANIZATION_REQUEST, DELETE_ORGANIZATION_SUCCESS, DELETE_ORGANIZATION_FAILURE],
  promise: (api: IApi) => api.crm.deleteOrganization(id),
}))

export const [MERGE_CRM_RECORD_REQUEST, MERGE_CRM_RECORD_SUCCESS, MERGE_CRM_RECORD_FAILURE] =
  createRequestTypes(prefix, 'MERGE_CRM_RECORD')

export const mergeCrmRecord = createAction(
  (collection: string, id: string, duplicateId: string) => ({
    type: [MERGE_CRM_RECORD_REQUEST, MERGE_CRM_RECORD_SUCCESS, MERGE_CRM_RECORD_FAILURE],
    promise: (api: IApi) => api.crm.mergeRecord(collection, id, duplicateId),
  }),
)

export const [
  UPDATE_PROSPECT_NEXT_STEPS_REQUEST,
  UPDATE_PROSPECT_NEXT_STEPS_SUCCESS,
  UPDATE_PROSPECT_NEXT_STEPS_FAILURE,
] = createRequestTypes(prefix, 'UPDATE_PROSPECT_NEXT_STEPS')

export const updateProspectNextSteps = createAction((companyId: string, data: object) => ({
  type: [
    UPDATE_PROSPECT_NEXT_STEPS_REQUEST,
    UPDATE_PROSPECT_NEXT_STEPS_SUCCESS,
    UPDATE_PROSPECT_NEXT_STEPS_FAILURE,
  ],
  promise: (api: IApi) => api.crm.updateProspectNextSteps(companyId, data),
}))

export const [
  CREATE_COMPANY_TEAM_MEMBER_REQUEST,
  CREATE_COMPANY_TEAM_MEMBER_SUCCESS,
  CREATE_COMPANY_TEAM_MEMBER_FAILURE,
] = createRequestTypes(prefix, 'CREATE_COMPANY_TEAM_MEMBER')

export const createCompanyTeamMember = createAction((companyId: string, data: object) => ({
  type: [
    CREATE_COMPANY_TEAM_MEMBER_REQUEST,
    CREATE_COMPANY_TEAM_MEMBER_SUCCESS,
    CREATE_COMPANY_TEAM_MEMBER_FAILURE,
  ],
  promise: (api: IApi) => api.crm.createCompanyTeamMember(companyId, data),
}))

export const [
  UPDATE_COMPANY_TEAM_MEMBER_REQUEST,
  UPDATE_COMPANY_TEAM_MEMBER_SUCCESS,
  UPDATE_COMPANY_TEAM_MEMBER_FAILURE,
] = createRequestTypes(prefix, 'UPDATE_COMPANY_TEAM_MEMBER')

export const updateCompanyTeamMember = createAction(
  (companyId: string, id: string, data: object) => ({
    type: [
      UPDATE_COMPANY_TEAM_MEMBER_REQUEST,
      UPDATE_COMPANY_TEAM_MEMBER_SUCCESS,
      UPDATE_COMPANY_TEAM_MEMBER_FAILURE,
    ],
    promise: (api: IApi) => api.crm.updateCompanyTeamMember(companyId, id, data),
  }),
)

export const [
  DELETE_COMPANY_TEAM_MEMBER_REQUEST,
  DELETE_COMPANY_TEAM_MEMBER_SUCCESS,
  DELETE_COMPANY_TEAM_MEMBER_FAILURE,
] = createRequestTypes(prefix, 'DELETE_COMPANY_TEAM_MEMBER')

export const deleteCompanyTeamMember = createAction((companyId: string, id: string) => ({
  type: [
    DELETE_COMPANY_TEAM_MEMBER_REQUEST,
    DELETE_COMPANY_TEAM_MEMBER_SUCCESS,
    DELETE_COMPANY_TEAM_MEMBER_FAILURE,
  ],
  promise: (api: IApi) => api.crm.deleteCompanyTeamMember(companyId, id),
}))

export const [
  CREATE_COMPANY_ORGANIZATION_REQUEST,
  CREATE_COMPANY_ORGANIZATION_SUCCESS,
  CREATE_COMPANY_ORGANIZATION_FAILURE,
] = createRequestTypes(prefix, 'CREATE_COMPANY_ORGANIZATION')

export const createCompanyOrganization = createAction((companyId: string, data: object) => ({
  type: [
    CREATE_COMPANY_ORGANIZATION_REQUEST,
    CREATE_COMPANY_ORGANIZATION_SUCCESS,
    CREATE_COMPANY_ORGANIZATION_FAILURE,
  ],
  promise: (api: IApi) => api.crm.createCompanyOrganization(companyId, data),
}))

export const [
  UPDATE_COMPANY_ORGANIZATION_REQUEST,
  UPDATE_COMPANY_ORGANIZATION_SUCCESS,
  UPDATE_COMPANY_ORGANIZATION_FAILURE,
] = createRequestTypes(prefix, 'UPDATE_COMPANY_ORGANIZATION')

export const updateCompanyOrganization = createAction(
  (companyId: string, id: string, data: object) => ({
    type: [
      UPDATE_COMPANY_ORGANIZATION_REQUEST,
      UPDATE_COMPANY_ORGANIZATION_SUCCESS,
      UPDATE_COMPANY_ORGANIZATION_FAILURE,
    ],
    promise: (api: IApi) => api.crm.updateCompanyOrganization(companyId, id, data),
  }),
)

export const [
  DELETE_COMPANY_ORGANIZATION_REQUEST,
  DELETE_COMPANY_ORGANIZATION_SUCCESS,
  DELETE_COMPANY_ORGANIZATION_FAILURE,
] = createRequestTypes(prefix, 'DELETE_COMPANY_ORGANIZATION')

export const deleteCompanyOrganization = createAction((companyId: string, id: string) => ({
  type: [
    DELETE_COMPANY_ORGANIZATION_REQUEST,
    DELETE_COMPANY_ORGANIZATION_SUCCESS,
    DELETE_COMPANY_ORGANIZATION_FAILURE,
  ],
  promise: (api: IApi) => api.crm.deleteCompanyOrganization(companyId, id),
}))

// Major Developments mutate through these and then reload the Overview feed,
// which is the single source of the card's entries — no separate slice to keep
// in step with it. The full history behind "View all" is fetched by the modal
// itself, since nothing else on the page needs it.
export const [
  LIST_COMPANY_MAJOR_DEVELOPMENTS_REQUEST,
  LIST_COMPANY_MAJOR_DEVELOPMENTS_SUCCESS,
  LIST_COMPANY_MAJOR_DEVELOPMENTS_FAILURE,
] = createRequestTypes(prefix, 'LIST_COMPANY_MAJOR_DEVELOPMENTS')

// Deliberately not reduced into a slice: only the expanded card wants the older
// entries, it wants them for as long as it stays expanded, and the card reads
// everything else off the Overview feed. The caller keeps the result.
export const listCompanyMajorDevelopments = createAction(
  (companyId: string, params: object = {}) => ({
    type: [
      LIST_COMPANY_MAJOR_DEVELOPMENTS_REQUEST,
      LIST_COMPANY_MAJOR_DEVELOPMENTS_SUCCESS,
      LIST_COMPANY_MAJOR_DEVELOPMENTS_FAILURE,
    ],
    promise: (api: IApi) => api.crm.listCompanyMajorDevelopments(companyId, params),
  }),
)

export const [
  CREATE_COMPANY_MAJOR_DEVELOPMENT_REQUEST,
  CREATE_COMPANY_MAJOR_DEVELOPMENT_SUCCESS,
  CREATE_COMPANY_MAJOR_DEVELOPMENT_FAILURE,
] = createRequestTypes(prefix, 'CREATE_COMPANY_MAJOR_DEVELOPMENT')

export const createCompanyMajorDevelopment = createAction(
  (companyId: string, data: { note: RawDraftContentState; occurredAt?: string }) => ({
    type: [
      CREATE_COMPANY_MAJOR_DEVELOPMENT_REQUEST,
      CREATE_COMPANY_MAJOR_DEVELOPMENT_SUCCESS,
      CREATE_COMPANY_MAJOR_DEVELOPMENT_FAILURE,
    ],
    promise: (api: IApi) => api.crm.createCompanyMajorDevelopment(companyId, data),
  }),
)

export const [
  UPDATE_COMPANY_MAJOR_DEVELOPMENT_REQUEST,
  UPDATE_COMPANY_MAJOR_DEVELOPMENT_SUCCESS,
  UPDATE_COMPANY_MAJOR_DEVELOPMENT_FAILURE,
] = createRequestTypes(prefix, 'UPDATE_COMPANY_MAJOR_DEVELOPMENT')

export const updateCompanyMajorDevelopment = createAction(
  (companyId: string, id: string, data: { note?: RawDraftContentState; occurredAt?: string }) => ({
    type: [
      UPDATE_COMPANY_MAJOR_DEVELOPMENT_REQUEST,
      UPDATE_COMPANY_MAJOR_DEVELOPMENT_SUCCESS,
      UPDATE_COMPANY_MAJOR_DEVELOPMENT_FAILURE,
    ],
    promise: (api: IApi) => api.crm.updateCompanyMajorDevelopment(companyId, id, data),
  }),
)

export const [
  DELETE_COMPANY_MAJOR_DEVELOPMENT_REQUEST,
  DELETE_COMPANY_MAJOR_DEVELOPMENT_SUCCESS,
  DELETE_COMPANY_MAJOR_DEVELOPMENT_FAILURE,
] = createRequestTypes(prefix, 'DELETE_COMPANY_MAJOR_DEVELOPMENT')

export const deleteCompanyMajorDevelopment = createAction((companyId: string, id: string) => ({
  type: [
    DELETE_COMPANY_MAJOR_DEVELOPMENT_REQUEST,
    DELETE_COMPANY_MAJOR_DEVELOPMENT_SUCCESS,
    DELETE_COMPANY_MAJOR_DEVELOPMENT_FAILURE,
  ],
  promise: (api: IApi) => api.crm.deleteCompanyMajorDevelopment(companyId, id),
}))

export const [
  CREATE_LOGGED_INTERACTION_REQUEST,
  CREATE_LOGGED_INTERACTION_SUCCESS,
  CREATE_LOGGED_INTERACTION_FAILURE,
] = createRequestTypes(prefix, 'CREATE_LOGGED_INTERACTION')

// Not reduced into a slice: every surface reads logged interactions off the
// Overview feed, so a successful write reloads that feed rather than patching
// state here — the same arrangement the Major Developments card uses.
export const createLoggedInteraction = createAction(
  (data: ILoggedInteractionPayload | { anchorKey: string; detail: string }) => ({
    type: [
      CREATE_LOGGED_INTERACTION_REQUEST,
      CREATE_LOGGED_INTERACTION_SUCCESS,
      CREATE_LOGGED_INTERACTION_FAILURE,
    ],
    promise: (api: IApi) => api.crm.createLoggedInteraction(data),
  }),
)

export const [
  UPDATE_LOGGED_INTERACTION_REQUEST,
  UPDATE_LOGGED_INTERACTION_SUCCESS,
  UPDATE_LOGGED_INTERACTION_FAILURE,
] = createRequestTypes(prefix, 'UPDATE_LOGGED_INTERACTION')

export const updateLoggedInteraction = createAction(
  (id: string, data: Partial<ILoggedInteractionPayload>) => ({
    type: [
      UPDATE_LOGGED_INTERACTION_REQUEST,
      UPDATE_LOGGED_INTERACTION_SUCCESS,
      UPDATE_LOGGED_INTERACTION_FAILURE,
    ],
    promise: (api: IApi) => api.crm.updateLoggedInteraction(id, data),
  }),
)

export const [
  DELETE_LOGGED_INTERACTION_REQUEST,
  DELETE_LOGGED_INTERACTION_SUCCESS,
  DELETE_LOGGED_INTERACTION_FAILURE,
] = createRequestTypes(prefix, 'DELETE_LOGGED_INTERACTION')

export const deleteLoggedInteraction = createAction((id: string) => ({
  type: [
    DELETE_LOGGED_INTERACTION_REQUEST,
    DELETE_LOGGED_INTERACTION_SUCCESS,
    DELETE_LOGGED_INTERACTION_FAILURE,
  ],
  promise: (api: IApi) => api.crm.deleteLoggedInteraction(id),
}))

export const [
  CREATE_CAPITAL_RAISE_REQUEST,
  CREATE_CAPITAL_RAISE_SUCCESS,
  CREATE_CAPITAL_RAISE_FAILURE,
] = createRequestTypes(prefix, 'CREATE_CAPITAL_RAISE')

export const createCapitalRaise = createAction((companyId: string, data: object) => ({
  type: [CREATE_CAPITAL_RAISE_REQUEST, CREATE_CAPITAL_RAISE_SUCCESS, CREATE_CAPITAL_RAISE_FAILURE],
  promise: (api: IApi) => api.crm.createCapitalRaise(companyId, data),
}))

export const [
  UPDATE_CAPITAL_RAISE_REQUEST,
  UPDATE_CAPITAL_RAISE_SUCCESS,
  UPDATE_CAPITAL_RAISE_FAILURE,
] = createRequestTypes(prefix, 'UPDATE_CAPITAL_RAISE')

export const updateCapitalRaise = createAction((companyId: string, id: string, data: object) => ({
  type: [UPDATE_CAPITAL_RAISE_REQUEST, UPDATE_CAPITAL_RAISE_SUCCESS, UPDATE_CAPITAL_RAISE_FAILURE],
  promise: (api: IApi) => api.crm.updateCapitalRaise(companyId, id, data),
}))

export const [
  DELETE_CAPITAL_RAISE_REQUEST,
  DELETE_CAPITAL_RAISE_SUCCESS,
  DELETE_CAPITAL_RAISE_FAILURE,
] = createRequestTypes(prefix, 'DELETE_CAPITAL_RAISE')

export const deleteCapitalRaise = createAction((companyId: string, id: string) => ({
  type: [DELETE_CAPITAL_RAISE_REQUEST, DELETE_CAPITAL_RAISE_SUCCESS, DELETE_CAPITAL_RAISE_FAILURE],
  promise: (api: IApi) => api.crm.deleteCapitalRaise(companyId, id),
}))

export const [
  UPDATE_CAPITAL_RAISE_INVESTMENT_REQUEST,
  UPDATE_CAPITAL_RAISE_INVESTMENT_SUCCESS,
  UPDATE_CAPITAL_RAISE_INVESTMENT_FAILURE,
] = createRequestTypes(prefix, 'UPDATE_CAPITAL_RAISE_INVESTMENT')

export const updateCapitalRaiseInvestment = createAction(
  (companyId: string, investmentId: string, data: object) => ({
    type: [
      UPDATE_CAPITAL_RAISE_INVESTMENT_REQUEST,
      UPDATE_CAPITAL_RAISE_INVESTMENT_SUCCESS,
      UPDATE_CAPITAL_RAISE_INVESTMENT_FAILURE,
    ],
    promise: (api: IApi) => api.crm.updateCapitalRaiseInvestment(companyId, investmentId, data),
  }),
)

export const [
  LOAD_COMPANY_OVERVIEW_FEED_REQUEST,
  LOAD_COMPANY_OVERVIEW_FEED_SUCCESS,
  LOAD_COMPANY_OVERVIEW_FEED_FAILURE,
] = createRequestTypes(prefix, 'LOAD_COMPANY_OVERVIEW_FEED')

export const loadCompanyOverviewFeed = createAction(
  (id: string, options: { skipLoader?: boolean } = {}) => ({
    type: [
      LOAD_COMPANY_OVERVIEW_FEED_REQUEST,
      LOAD_COMPANY_OVERVIEW_FEED_SUCCESS,
      LOAD_COMPANY_OVERVIEW_FEED_FAILURE,
    ],
    promise: (api: IApi) => api.crm.companyOverviewFeed(id),
    // Background refreshes after add/complete pass skipLoader so the timeline
    // doesn't flash its skeleton — the row updates in place instead.
    params: { skipLoader: options.skipLoader },
  }),
)

export const [
  REGENERATE_COMPANY_DOSSIER_REQUEST,
  REGENERATE_COMPANY_DOSSIER_SUCCESS,
  REGENERATE_COMPANY_DOSSIER_FAILURE,
] = createRequestTypes(prefix, 'REGENERATE_COMPANY_DOSSIER')

export const regenerateCompanyDossier = createAction((id: string) => ({
  type: [
    REGENERATE_COMPANY_DOSSIER_REQUEST,
    REGENERATE_COMPANY_DOSSIER_SUCCESS,
    REGENERATE_COMPANY_DOSSIER_FAILURE,
  ],
  promise: (api: IApi) => api.crm.regenerateCompanyDossier(id),
}))

export const [
  REGENERATE_COMPANY_CATCH_UP_REQUEST,
  REGENERATE_COMPANY_CATCH_UP_SUCCESS,
  REGENERATE_COMPANY_CATCH_UP_FAILURE,
] = createRequestTypes(prefix, 'REGENERATE_COMPANY_CATCH_UP')

export const regenerateCompanyCatchUp = createAction((id: string) => ({
  type: [
    REGENERATE_COMPANY_CATCH_UP_REQUEST,
    REGENERATE_COMPANY_CATCH_UP_SUCCESS,
    REGENERATE_COMPANY_CATCH_UP_FAILURE,
  ],
  promise: (api: IApi) => api.crm.regenerateCompanyCatchUp(id),
}))

export const [
  UPDATE_COMPANY_DOSSIER_REQUEST,
  UPDATE_COMPANY_DOSSIER_SUCCESS,
  UPDATE_COMPANY_DOSSIER_FAILURE,
] = createRequestTypes(prefix, 'UPDATE_COMPANY_DOSSIER')

export const updateCompanyDossier = createAction((id: string, dossier: string) => ({
  type: [
    UPDATE_COMPANY_DOSSIER_REQUEST,
    UPDATE_COMPANY_DOSSIER_SUCCESS,
    UPDATE_COMPANY_DOSSIER_FAILURE,
  ],
  promise: (api: IApi) => api.crm.updateCompanyDossier(id, dossier),
}))

export const [SHOW_PERSON_REQUEST, SHOW_PERSON_SUCCESS, SHOW_PERSON_FAILURE] = createRequestTypes(
  prefix,
  'SHOW_PERSON',
)

export const showPerson = createAction((id: string, options: { skipLoader?: boolean } = {}) => ({
  type: [SHOW_PERSON_REQUEST, SHOW_PERSON_SUCCESS, SHOW_PERSON_FAILURE],
  promise: (api: IApi) => api.crm.showPerson(id),
  // Background refreshes (e.g. after an inline notes save) pass skipLoader so
  // the page doesn't flash its full-page spinner.
  params: { skipLoader: options.skipLoader },
}))

export const [UPDATE_PERSON_REQUEST, UPDATE_PERSON_SUCCESS, UPDATE_PERSON_FAILURE] =
  createRequestTypes(prefix, 'UPDATE_PERSON')

export const updatePerson = createAction((id: string, data: object) => ({
  type: [UPDATE_PERSON_REQUEST, UPDATE_PERSON_SUCCESS, UPDATE_PERSON_FAILURE],
  promise: (api: IApi) => api.crm.updatePerson(id, data),
}))

export const [
  CREATE_PERSON_AFFILIATION_REQUEST,
  CREATE_PERSON_AFFILIATION_SUCCESS,
  CREATE_PERSON_AFFILIATION_FAILURE,
] = createRequestTypes(prefix, 'CREATE_PERSON_AFFILIATION')

export const createPersonAffiliation = createAction((personId: string, data: object) => ({
  type: [
    CREATE_PERSON_AFFILIATION_REQUEST,
    CREATE_PERSON_AFFILIATION_SUCCESS,
    CREATE_PERSON_AFFILIATION_FAILURE,
  ],
  promise: (api: IApi) => api.crm.createPersonAffiliation(personId, data),
}))

export const [
  UPDATE_PERSON_AFFILIATION_REQUEST,
  UPDATE_PERSON_AFFILIATION_SUCCESS,
  UPDATE_PERSON_AFFILIATION_FAILURE,
] = createRequestTypes(prefix, 'UPDATE_PERSON_AFFILIATION')

export const updatePersonAffiliation = createAction(
  (personId: string, id: string, data: object) => ({
    type: [
      UPDATE_PERSON_AFFILIATION_REQUEST,
      UPDATE_PERSON_AFFILIATION_SUCCESS,
      UPDATE_PERSON_AFFILIATION_FAILURE,
    ],
    promise: (api: IApi) => api.crm.updatePersonAffiliation(personId, id, data),
  }),
)

export const [
  LOAD_PERSON_OVERVIEW_FEED_REQUEST,
  LOAD_PERSON_OVERVIEW_FEED_SUCCESS,
  LOAD_PERSON_OVERVIEW_FEED_FAILURE,
] = createRequestTypes(prefix, 'LOAD_PERSON_OVERVIEW_FEED')

export const loadPersonOverviewFeed = createAction(
  (id: string, options: { skipLoader?: boolean } = {}) => ({
    type: [
      LOAD_PERSON_OVERVIEW_FEED_REQUEST,
      LOAD_PERSON_OVERVIEW_FEED_SUCCESS,
      LOAD_PERSON_OVERVIEW_FEED_FAILURE,
    ],
    promise: (api: IApi) => api.crm.personOverviewFeed(id),
    // Background refreshes after add/complete pass skipLoader so the timeline
    // doesn't flash its skeleton — the row updates in place instead.
    params: { skipLoader: options.skipLoader },
  }),
)

export const [
  REGENERATE_PERSON_DOSSIER_REQUEST,
  REGENERATE_PERSON_DOSSIER_SUCCESS,
  REGENERATE_PERSON_DOSSIER_FAILURE,
] = createRequestTypes(prefix, 'REGENERATE_PERSON_DOSSIER')

export const regeneratePersonDossier = createAction((id: string) => ({
  type: [
    REGENERATE_PERSON_DOSSIER_REQUEST,
    REGENERATE_PERSON_DOSSIER_SUCCESS,
    REGENERATE_PERSON_DOSSIER_FAILURE,
  ],
  promise: (api: IApi) => api.crm.regeneratePersonDossier(id),
}))

export const [
  UPDATE_PERSON_DOSSIER_REQUEST,
  UPDATE_PERSON_DOSSIER_SUCCESS,
  UPDATE_PERSON_DOSSIER_FAILURE,
] = createRequestTypes(prefix, 'UPDATE_PERSON_DOSSIER')

export const updatePersonDossier = createAction((id: string, dossier: string) => ({
  type: [
    UPDATE_PERSON_DOSSIER_REQUEST,
    UPDATE_PERSON_DOSSIER_SUCCESS,
    UPDATE_PERSON_DOSSIER_FAILURE,
  ],
  promise: (api: IApi) => api.crm.updatePersonDossier(id, dossier),
}))

export const [
  REGENERATE_PERSON_CATCH_UP_REQUEST,
  REGENERATE_PERSON_CATCH_UP_SUCCESS,
  REGENERATE_PERSON_CATCH_UP_FAILURE,
] = createRequestTypes(prefix, 'REGENERATE_PERSON_CATCH_UP')

export const regeneratePersonCatchUp = createAction((id: string) => ({
  type: [
    REGENERATE_PERSON_CATCH_UP_REQUEST,
    REGENERATE_PERSON_CATCH_UP_SUCCESS,
    REGENERATE_PERSON_CATCH_UP_FAILURE,
  ],
  promise: (api: IApi) => api.crm.regeneratePersonCatchUp(id),
}))

// ── Mention review queue (Mention review page) ────────────────────────────────

export const [LIST_MENTIONS_REQUEST, LIST_MENTIONS_SUCCESS, LIST_MENTIONS_FAILURE] =
  createRequestTypes(prefix, 'LIST_MENTIONS')

export const listMentions = createAction((data: object = {}) => ({
  type: [LIST_MENTIONS_REQUEST, LIST_MENTIONS_SUCCESS, LIST_MENTIONS_FAILURE],
  promise: (api: IApi) => api.mentions.list(data),
  params: data,
}))

export const [
  RESOLVE_MENTION_ROLE_REQUEST,
  RESOLVE_MENTION_ROLE_SUCCESS,
  RESOLVE_MENTION_ROLE_FAILURE,
] = createRequestTypes(prefix, 'RESOLVE_MENTION_ROLE')

export const resolveMentionRole = createAction(
  (id: string, data: { role: MentionRole; kind: MentionEntityKind; entityId: string }) => ({
    type: [
      RESOLVE_MENTION_ROLE_REQUEST,
      RESOLVE_MENTION_ROLE_SUCCESS,
      RESOLVE_MENTION_ROLE_FAILURE,
    ],
    promise: (api: IApi) => api.mentions.resolveRole(id, data),
    params: { id },
  }),
)

export const [
  UPDATE_MENTION_STATUS_REQUEST,
  UPDATE_MENTION_STATUS_SUCCESS,
  UPDATE_MENTION_STATUS_FAILURE,
] = createRequestTypes(prefix, 'UPDATE_MENTION_STATUS')

export const updateMentionStatus = createAction((id: string, status: MentionStatus) => ({
  type: [
    UPDATE_MENTION_STATUS_REQUEST,
    UPDATE_MENTION_STATUS_SUCCESS,
    UPDATE_MENTION_STATUS_FAILURE,
  ],
  promise: (api: IApi) => api.mentions.updateStatus(id, { status }),
  params: { id },
}))

export const [
  CREATE_MENTION_LEAD_REQUEST,
  CREATE_MENTION_LEAD_SUCCESS,
  CREATE_MENTION_LEAD_FAILURE,
] = createRequestTypes(prefix, 'CREATE_MENTION_LEAD')

export const createMentionLead = createAction(
  (id: string, data: { role: MentionPartyRole; kind: MentionEntityKind; name?: string }) => ({
    type: [CREATE_MENTION_LEAD_REQUEST, CREATE_MENTION_LEAD_SUCCESS, CREATE_MENTION_LEAD_FAILURE],
    promise: (api: IApi) => api.mentions.createLead(id, data),
    params: { id },
  }),
)
